/** Máximo de dígitos nacionais (DDD + número). */
export const BRAZIL_PHONE_MAX_DIGITS = 11

function stripBrazilCountryCode(digits: string): string {
  // Meta: prefixo errado "015" + 11 dígitos nacionais (ex.: 01531996934977).
  if (/^015\d{11}$/.test(digits)) {
    return digits.slice(3)
  }

  let normalized = digits
  if (normalized.startsWith("0")) {
    normalized = normalized.replace(/^0+/, "")
  }
  if (normalized.startsWith("55") && normalized.length > BRAZIL_PHONE_MAX_DIGITS) {
    normalized = normalized.slice(2)
  }
  return normalized
}

function primaryPhoneToken(raw: string): string {
  return raw.split(/#+/)[0]?.trim() ?? ""
}

/**
 * Formata entrada de telefone BR enquanto o usuário digita.
 * Celular: (DD) 9XXXX-XXXX; fixo: (DD) XXXX-XXXX (heurística: primeiro dígito após DDD é 9 → celular).
 */
export function formatBrazilPhoneInput(raw: string): string {
  const digits = raw.replace(/\D/g, "").slice(0, BRAZIL_PHONE_MAX_DIGITS)
  if (digits.length === 0) return ""

  const dd = digits.slice(0, 2)
  if (digits.length <= 2) {
    return `(${dd}`
  }

  const rest = digits.slice(2)
  const isMobile = rest[0] === "9"
  const maxRest = isMobile ? 9 : 8
  const r = rest.slice(0, maxRest)

  if (isMobile) {
    if (r.length <= 5) return `(${dd}) ${r}`
    return `(${dd}) ${r.slice(0, 5)}-${r.slice(5)}`
  }
  if (r.length <= 4) return `(${dd}) ${r}`
  return `(${dd}) ${r.slice(0, 4)}-${r.slice(4)}`
}

export function brazilPhoneDigits(value: string): string {
  const digits = value.replace(/\D/g, "")
  return stripBrazilCountryCode(digits).slice(0, BRAZIL_PHONE_MAX_DIGITS)
}

/**
 * Telefone opcional: vazio OK; caso contrário 10 (fixo) ou 11 (celular com 9 após DDD).
 * Retorna string formatada para persistência alinhada ao restante do app.
 */
export function normalizeBrazilPhoneOptional(
  trimmed: string | null | undefined,
): { ok: true; value: string | null } | { ok: false; error: string } {
  const t = primaryPhoneToken(trimmed?.trim() ?? "").replace(/^p:/i, "")
  if (!t) {
    return { ok: true, value: null }
  }
  const d = brazilPhoneDigits(t)
  if (d.length < 10 || d.length > 11) {
    return { ok: false, error: "Telefone inválido. Use DDD + número (10 ou 11 dígitos)." }
  }
  if (d.length === 11 && d[2] !== "9") {
    return { ok: false, error: "Celular inválido: após o DDD deve começar com 9." }
  }
  return { ok: true, value: formatBrazilPhoneInput(d) }
}

/** Telefone obrigatório (mesma regra de dígitos que `normalizeBrazilPhoneOptional`). */
export function normalizeBrazilPhoneRequired(
  trimmed: string | null | undefined,
): { ok: true; value: string } | { ok: false; error: string } {
  const t = trimmed?.trim() ?? ""
  if (!t) {
    return { ok: false, error: "Telefone é obrigatório." }
  }
  const opt = normalizeBrazilPhoneOptional(t)
  if (!opt.ok) return opt
  if (!opt.value) {
    return { ok: false, error: "Telefone é obrigatório." }
  }
  return { ok: true, value: opt.value }
}
